import { useMemo, useEffect, useCallback } from 'react'

// Components
import { createAssetsColumnDefinitions } from './constants/columnDefinitions'

// Services
import { useGetAssetReports } from 'lib/services/api/reports-service/assetReports'

// Utils
import dayjs from 'lib/dayjs'
import { samplePreparationCaster } from 'lib/utils/samplePreparationCaster'
import { useAssetsReportsStore } from 'store/assetsReports/assetsReports.store'

// Types
import { getAssetTypeLabel } from 'lib/utils/getAssetTypeLabel'
import { ImplantStatus } from 'common/types'
import { getReadableImplantStatus } from 'lib/utils/getReadableImplantStatus'
import { WASTED_REASON_LIST } from 'common/disposition'
import {
  AssetReportOutput,
  AssetReportsInput,
  MappedAssets,
  ReportAsset,
} from './AssetsReports.types'

// Other
import { saveAs } from 'file-saver'
import { enqueueSnackbar } from 'notistack'
import { useFlags } from 'launchdarkly-react-client-sdk'

export const useAssetsReportsLogic = () => {
  const { filters, setLoading } = useAssetsReportsStore()
  const flags = useFlags()

  const serverParams = useMemo(() => {
    const params = {} as AssetReportsInput

    if (filters.startDate) {
      params.startDate = dayjs(filters.startDate).format(
        'YYYY-MM-DD'
      ) as unknown as dayjs.Dayjs
    }

    if (filters.endDate) {
      params.endDate = dayjs(filters.endDate).format(
        'YYYY-MM-DD'
      ) as unknown as dayjs.Dayjs
    }

    params.pageSize = 1000000

    return params
  }, [filters])

  const { data, isLoading, isError } = useGetAssetReports({
    ...serverParams,
  })

  const assets = data as AssetReportOutput[]

  const applyFilters = useCallback(
    (assets: AssetReportOutput[]): AssetReportOutput[] => {
      if (!assets) return []

      return assets.filter((asset) => {
        // Surgeon filter
        if (filters.surgeonName?.length) {
          const fullSurgeonName =
            asset.surgeon?.firstName && asset.surgeon?.lastName
              ? `${asset.surgeon.firstName} ${asset.surgeon.lastName}`
              : ''
          const matches = filters.surgeonName.some((selectedSurgeon) =>
            fullSurgeonName.includes(selectedSurgeon)
          )
          if (!matches) {
            return false
          }
        }

        // Procedure filter
        if (filters.procedure?.length) {
          const procedureDescription =
            asset.surgery?.procedures?.[0]?.description ?? ''
          if (!filters.procedure.includes(procedureDescription)) {
            return false
          }
        }

        // Location filter
        if (filters.location?.length) {
          const location = asset.surgery.visit?.location?.department ?? ''
          if (!filters.location.includes(location)) {
            return false
          }
        }

        // Company name filter
        if (filters.companyName?.length) {
          const companyName = asset.companyName ?? ''
          if (!filters.companyName.includes(companyName)) {
            return false
          }
        }

        // Asset type filter
        if (filters.assetType?.length) {
          const assetTypeLabel = getAssetTypeLabel(asset.assets?.[0]?.assetType)
          if (!filters.assetType.includes(assetTypeLabel || '')) {
            return false
          }
        }

        // Disposition filter
        if (filters.disposition?.length) {
          const disposition = getReadableImplantStatus(
            asset.assets?.[0]?.implantStatus as ImplantStatus
          )
          if (!filters.disposition.includes(disposition as string)) {
            return false
          }
        }

        return true
      })
    },
    [filters]
  )

  const filteredAssets = useMemo(() => {
    return applyFilters(assets)
  }, [assets, applyFilters])

  const mappedAssets: MappedAssets[] = useMemo(() => {
    if (!filteredAssets) return []

    const getImplantStatus = (assets: ReportAsset[]) => {
      return getReadableImplantStatus(
        assets?.[0]?.implantStatus as ImplantStatus
      ) as ImplantStatus
    }

    const getWastedReason = (assets: ReportAsset[]) => {
      let wastedReason = '-'

      if (assets?.length > 1) {
        wastedReason =
          assets?.find((asset) => asset.implantStatus === 'WASTED')
            ?.wastedReason ?? '-'
      } else {
        wastedReason = assets?.[0]?.wastedReason ?? '-'
      }

      if (wastedReason === '-') return wastedReason

      return (
        WASTED_REASON_LIST.find((reason) => reason.id === wastedReason)?.name ??
        wastedReason
      )
    }

    return filteredAssets.flatMap((asset: AssetReportOutput) => {
      const individualAssets = asset.assets || []
      const room: string | undefined = flags.enablePatientStatus
        ? asset.surgery.patientStatusHistory?.[0]?.info?.room?.name || '-'
        : asset.surgery.visit?.location?.room || ''

      return individualAssets.map((individualAsset) => ({
        date: asset.createdAt
          ? dayjs.utc(asset.createdAt).local().format('MM/DD/YYYY')
          : '-',
        time: asset.createdAt
          ? dayjs.utc(asset.createdAt).local().format('hh:mm A')
          : '-',
        facility: asset.surgery.visit?.location?.department ?? '-',
        room: room ?? '-',
        patientName:
          asset.patient?.firstName && asset.patient?.lastName
            ? `${asset.patient.lastName}, ${asset.patient.firstName}`
            : '-',
        patientDOB: asset.patient?.dateOfBirth
          ? dayjs(asset.patient.dateOfBirth).format('YYYY/MM/DD')
          : '-',
        mrn: asset.patient?.idMR ?? '-',
        procedureType: asset.surgery?.procedures?.[0]?.description ?? '-',
        surgeon:
          asset.surgeon?.firstName || asset.surgeon?.lastName
            ? `${asset.surgeon?.firstName} ${asset.surgeon?.lastName}`
            : '-',
        nurse: '-',
        scrubTech: asset.surgery.status.scrubTechnician ?? '-',
        productType: getAssetTypeLabel(individualAsset.assetType) ?? '-',
        manufacturer: asset.companyName ?? '-',
        productDescription: asset.deviceDescription ?? '-',
        modelNumber: asset.versionModelNumber ?? '-',
        catalogNumber: asset.catalogNumber ?? '-',
        lot: asset.lotBatch ?? '-',
        serial: individualAsset.serialNumber ?? '-',
        expiration: asset?.expirationDate
          ? dayjs(asset.expirationDate).format('MM/DD/YYYY')
          : '-',
        udi: individualAsset.udi ?? '-',
        disposition: getImplantStatus([individualAsset]) ?? '-',
        siteLocation: individualAsset.siteLocation ?? '-',
        implantSite: individualAsset.implantSite ?? '-',
        wastedReason: getWastedReason([individualAsset]) ?? '-',
        temperatureOk:
          individualAsset.assetType === 'biological'
            ? individualAsset.temperatureOk
              ? 'Yes'
              : 'No'
            : '-',
        packagingOk:
          individualAsset.assetType === 'biological'
            ? individualAsset.packagingOk
              ? 'Yes'
              : 'No'
            : '-',
        samplePreparation:
          samplePreparationCaster(individualAsset.samplePreparation) ?? '-',
        assetType: getAssetTypeLabel(individualAsset.assetType) ?? '-',
        itemCost: (asset.totalCost ?? 0) / (asset.total ?? 1),
      }))
    })
  }, [filteredAssets, flags.enablePatientStatus])

  const handleDownload = async () => {
    try {
      if (!mappedAssets || mappedAssets.length === 0) {
        enqueueSnackbar('No data available to download', {
          variant: 'error',
        })
        return
      }

      const columnDefinitions = createAssetsColumnDefinitions()
      const headers = columnDefinitions.map((col) => col.headerName)
      const fieldNames = columnDefinitions.map((col) => col.field)

      const csvData = mappedAssets.map((asset) =>
        fieldNames.map((field) => {
          const value = (asset as any)[field]

          if (field === 'itemCost' && value !== undefined) {
            return `$${Number(value).toFixed(2)}`
          }

          if (
            [
              'mrn',
              'modelNumber',
              'catalogNumber',
              'lot',
              'serial',
              'udi',
            ].includes(field) &&
            value
          ) {
            return ` ${value}`
          }

          return value || '-'
        })
      )

      const csvContent = [headers, ...csvData]
        .map((row) =>
          row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(',')
        )
        .join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })

      let filename = 'products-report'
      if (filters.startDate && filters.endDate) {
        const startDate = dayjs(filters.startDate).format('YYYY-DD-MM')
        const endDate = dayjs(filters.endDate).format('YYYY-DD-MM')
        filename = `products-report-${startDate}-to-${endDate}`
      } else if (filters.startDate) {
        const startDate = dayjs(filters.startDate).format('YYYY-DD-MM')
        filename = `products-report-from-${startDate}`
      } else if (filters.endDate) {
        const endDate = dayjs(filters.endDate).format('YYYY-DD-MM')
        filename = `products-report-to-${endDate}`
      } else {
        const timestamp = dayjs().format('YYYY-DD-MM-HH-mm-ss')
        filename = `products-report-${timestamp}`
      }

      saveAs(blob, `${filename}.csv`)

      enqueueSnackbar('CSV file downloaded successfully', {
        variant: 'success',
      })
    } catch (error) {
      console.error('Error generating CSV:', error)
      enqueueSnackbar('Error generating CSV file', {
        variant: 'error',
      })
    }
  }

  useEffect(() => {
    setLoading(isLoading)
  }, [isLoading, setLoading])

  return {
    mappedAssets,
    originalAssets: assets,
    isLoading,
    isError,
    handleDownload,
  }
}
